'use client';

import { Box } from '@/components/ui/layout';
import { GUEST_GRADIENT_PROPERTY } from '@/features/account/store/user-preferences';
import { MINERVA_GUEST_GRADIENT } from '@/features/branding/partner-tokens';

import styles from './guest-landing-text-panel.layout.module.css';

/**
 * Guest landing left panel.
 *
 * The panel renders inside the PUBLIC route group, whose layout never receives
 * tenant branding: `/login` is pre-session, so the server cannot resolve which
 * partner (if any) this visitor belongs to. The gradient therefore comes from
 * browser-only state (the last-tenant branding snapshot), and the scheme is
 * selected by the bootstrap that stamps it rather than in JS here.
 *
 * LIMITATION, deliberate and documented: pre-login branding is therefore
 * best-effort. The gradient reads the bootstrap snapshot that the previous
 * authenticated paint wrote on this device, so a returning Equifax user sees
 * the partner gradient while a first visit or a shared device sees the
 * Minerva rainbow.
 *
 * The snapshot is cleared on sign-out (every exit path, including forced exit
 * and session expiry) and by any unbound authenticated paint, so a device that
 * has moved to another tenant does not keep painting the previous partner's
 * gradient here. Nothing here matches on partner and nothing can: `/login` is
 * pre-session, so there is no partner to match a record against. Attribution on
 * the record retires un-keyed legacy snapshots only; the clears are what carry
 * the cross-tenant guarantee.
 *
 * WHY A CSS VARIABLE RATHER THAN A COMPUTED VALUE: this component previously
 * resolved the gradient from `localStorage` in a `useMemo`. It never reached
 * the screen on a hard load. The server has no `localStorage`, so SSR always
 * emitted the rainbow, and React 18 does NOT patch a mismatched class or inline
 * style during hydration — it warns the tree "won't be patched up" and keeps
 * the SERVER value, and the memo (keyed on the colour scheme) never re-ran to
 * correct it. The value now arrives on `<html>` from the beforeInteractive
 * bootstrap, which React does not own and never reconciles, while this element
 * renders ONE static class in both passes. The literal fallback keeps an
 * unbound document identical to what it rendered before this MR.
 */
export function GuestLandingTextPanel({ title }: { title: string }) {
  const backgroundImage = `var(${GUEST_GRADIENT_PROPERTY}, ${MINERVA_GUEST_GRADIENT})`;

  return (
    <Box
      role="img"
      aria-label="background image gradient"
      className={styles.panel}
      style={{ backgroundImage }}
    >
      <h1
        className="max-w-124 text-left font-bold text-5xl leading-tight"
        data-testid="user-landing-title"
      >
        {title}
      </h1>
      <div className="max-w-124 text-left font-medium text-xl">
        Frictionless screening and anti-money laundering investigations are just
        a few clicks away.
      </div>
    </Box>
  );
}
